import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import { cn } from '../../lib';

function CartRoot({
  children,
  open,
  onOpenChange,
}: {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children}
    </Sheet>
  );
}

function CartTrigger({
  children,
  className,
  ...props
}: { children: ReactElement } & HTMLAttributes<HTMLButtonElement>) {
  return (
    <SheetTrigger
      className={className}
      render={children as ReactElement<Record<string, unknown>>}
      {...props}
    />
  );
}

function CartContent({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <SheetContent
      side="right"
      className={cn('!z-[999999] w-full', className)}
      {...props}
    >
      <SheetTitle className="sr-only">Cart</SheetTitle>
      <SheetDescription className="sr-only">
        View your cart and checkout
      </SheetDescription>
      {children}
    </SheetContent>
  );
}

function CartHeader({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <SheetHeader className={cn(className)} {...props}>
      {children}
    </SheetHeader>
  );
}

function CartTitle({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLHeadingElement>) {
  return (
    <SheetTitle className={cn('', className)} {...props}>
      {children}
    </SheetTitle>
  );
}

function CartItems({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <SheetBody
      className={cn('mt-4 flex h-0 flex-grow flex-col', className)}
      {...props}
    >
      {children}
    </SheetBody>
  );
}

function CartItemsList({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col', className)} {...props}>
      {children}
    </div>
  );
}

function CartEmptyWrapper({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        '!m-0 scrollbar-y-slim grid gap-6 overflow-y-auto pb-24 text-center transition-all duration-200 ease-in-out',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function CartEmptyHeading({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn(className)} {...props}>
      Your cart is empty.
    </h3>
  );
}

function CartFooter({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('border-t', className)} {...props}>
      {children}
    </div>
  );
}

function CartClose({
  children,
  className,
  ...props
}: { children: ReactElement } & HTMLAttributes<HTMLButtonElement>) {
  return (
    <SheetClose
      className={className}
      render={children as ReactElement<Record<string, unknown>>}
      {...props}
    />
  );
}

export function CartShared({
  children,
  className,
  ...props
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <CartRoot className={className} {...props}>
      {children}
    </CartRoot>
  );
}

CartShared.Root = CartRoot;
CartShared.Trigger = CartTrigger;
CartShared.Content = CartContent;
CartShared.Header = CartHeader;
CartShared.Title = CartTitle;
CartShared.Items = CartItems;
CartShared.ItemsList = CartItemsList;
CartShared.Footer = CartFooter;
CartShared.Close = CartClose;
CartShared.EmptyWrapper = CartEmptyWrapper;
CartShared.EmptyHeading = CartEmptyHeading;
